import _ from 'lodash';
import store from '../../store';
import { OVERVIEW_PANEL_GET_CURRENT, OVERVIEW_PANEL_IS_VISIBLE } from '../../store/modules/overview-panel/types';
import worldMap from '../core/map';
import { pinBuilder } from '../core/map/pinBuilder';
import ColorBrewer from '../core/tools/colorbrewer';
import environment from '../environment';
import Leaflet from './Leaflet';
import templates from '../core/templates';
import translate from '../core/tools/translate';
import { VehicleTypeEnum } from '../core/vehicleType/constants';
import { StoreTypeEnum } from '../../enuns/store.enun';

const LayerControls = (name) => require('../controls').get(name);
const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('./index').default.get(name, currentMap);
};

const name = 'MallCenters';

class MallCentersLayer {
    constructor (currentMap) {
        let _total = 0;
        const mapId = +currentMap._id;

        // Pin com valor (VMM ativa) é desenhado por cima desta imagem.
        const pinClusterImage = new Image();
        pinClusterImage.src = `${environment.get('IMAGES_HOST')}/pin_centro_comercial_cluster.png`;
        pinClusterImage.width = 50;
        pinClusterImage.height = 64;

        const isVmmEnabled = () => {
            const controls = LayerControls(name).getControls(mapId) || {};
            return ((controls.checkbox || {}).tagValue || {}).status;
        };

        const getBrandLogo = (brand) => {
            return environment.get('CONFIG.USERVEHICLETYPE') === VehicleTypeEnum.Moto
                ? `${environment.get('IMAGES_HOST')}/motos/logo_${String(brand).toUnderscoreCase()}.png`
                : `${environment.get('IMAGES_HOST')}/logo_${String(brand).toUnderscoreCase()}.png`;
        };

        // O endpoint retorna 1 feature por LOJA. Como as lojas de um mesmo shopping
        // compartilham a localização, agrupamos por MallName -> 1 ponto por shopping.
        const aggregateByMall = (featureCollection) => {
            const stores = (featureCollection.features || [])
                .filter(f => f.properties.MallName && f.properties.IdTipoLoja !== StoreTypeEnum.Oficina);

            const features = _(stores)
                .groupBy('properties.MallName')
                .map((mallStores, mallName) => {
                    const coordinates = [
                        _.meanBy(mallStores, s => s.geometry.coordinates[0]),
                        _.meanBy(mallStores, s => s.geometry.coordinates[1])
                    ];

                    const brands = _(mallStores)
                        .groupBy('properties.Brand')
                        .map((v, brand) => ({
                            name: String(brand).toCapitalCase(),
                            icon: getBrandLogo(brand),
                            sales: _.sumBy(v, 'properties.MediaMensal'),
                            total: _.size(v)
                        }))
                        .orderBy('sales', ['desc'])
                        .value();

                    return {
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates },
                        properties: {
                            Id: mallName,
                            MallName: mallName,
                            City: mallStores[0].properties.City,
                            State: mallStores[0].properties.State,
                            MediaMensal: Math.round(_.sumBy(mallStores, 'properties.MediaMensal')) || 0,
                            Total: _.sumBy(mallStores, 'properties.Total') || 0,
                            TotalStores: _.sumBy(brands, 'total'),
                            brands
                        }
                    };
                })
                .value();

            return { type: 'FeatureCollection', features };
        };

        // Ícone do PONTO individual (cluster desfeito no zoom/decluster).
        // VMM off -> pin limpo de hoje; VMM on -> pin com o MediaMensal do centro.
        const getIcon = (properties) => {
            if (!isVmmEnabled()) {
                return `${environment.get('IMAGES_HOST')}/pin_centro_comercial.png`;
            }

            if (!_total) {
                _total = Math.round(_.sumBy(get(name, currentMap).getCurrentFeatures(), 'properties.MediaMensal'));
            }

            // total = soma geral -> imageWithValue desenha valor + % (value/total) + donut, igual ao cluster.
            return pinBuilder.create.pin.imageWithValue(
                { total: _total, value: properties.MediaMensal },
                pinClusterImage
            ).options.iconUrl;
        };

        // Ícone do CLUSTER (vários centros agrupados).
        // VMM off -> contagem de centros; VMM on -> SOMA de MediaMensal + % (igual cluster de vendas).
        const clusterTemplate = (cluster) => {
            const features = cluster.getAllChildMarkers();

            if (!isVmmEnabled()) {
                const childCount = cluster.getChildCount();

                // Cluster de vários centros comerciais: mostra a contagem de centros (pins) agrupados.
                if (childCount > 1) {
                    return pinBuilder.create.pin.imageWithValue({ value: childCount }, pinClusterImage);
                }

                // Centro comercial único: pin limpo, sem valor.
                return L.icon({
                    iconUrl: `${environment.get('IMAGES_HOST')}/pin_centro_comercial.png`,
                    iconSize: L.point(50, 64),
                    iconAnchor: [25, 60]
                });
            }

            if (!_total) {
                _total = Math.round(_.sumBy(get(name, currentMap).getCurrentFeatures(), 'properties.MediaMensal'));
            }

            const data = {
                total: _total,
                value: Math.round(_.sumBy(features, 'feature.properties.MediaMensal'))
            };

            if (data.value === 0) {
                return L.icon({
                    iconUrl: `${environment.get('IMAGES_HOST')}/icon_blank.png`,
                    iconSize: L.point(0, 0),
                    iconAnchor: [0, 0]
                });
            }

            return pinBuilder.create.pin.imageWithValue(data, pinClusterImage);
        };

        // Popup do PIN individual (vale para VMM marcada ou não).
        const popupTemplate = (properties) => {
            const brands = properties.brands || [];
            const totalStores = properties.TotalStores;
            const totalSales = properties.Total;

            const values = {
                mallName: properties.MallName,
                brands,
                period: LayerControls(name).currentPeriod,
                totalBrands: _.size(brands),
                totalStores,
                totalAvgSalesInCluster: _.sumBy(brands, 'sales'),
                totalAvgSales: totalStores ? _.sumBy(brands, 'sales') / totalStores : 0,
                totalSales,
                salesEfficiency: totalSales
            };

            return $(templates('map-layer-stores-in-mall-cluster-popup', values));
        };

        const onComplete = () => {
            _total = 0;

            if (store.getters[OVERVIEW_PANEL_GET_CURRENT] === name && store.getters[OVERVIEW_PANEL_IS_VISIBLE] && LayerControls(name).getControls(mapId).button.overview.enable) {
                LayerControls(name).toggleOverview();
            }
        };

        const onSuccess = res => aggregateByMall(res);

        return new Leaflet.D3Layer({
            map: currentMap,
            remote: {
                method: 'POST',
                url: `${environment.get('API_HOST')}/lojaconcessionaria/getmallcenterswithstores`,
                params: {
                    bandeiras: (() => {
                        let flags = [];
                        _.forEach(environment.get('CONFIG.DEALERS'), flag => flags.push(flag));
                        _.forEach(environment.get('CONFIG.COMPETITORS'), flag => flags.push(flag));

                        return flags.join(',');
                    })(),
                    ...LayerControls(name).dateApiParams,
                    bbox: 'LINESTRING(-13.095703125 5.266007882805511, -86.923828125 -33.46810795527895)'
                }
            },
            filter: {
                location: true
            },
            showAll: true,
            uniqueField: 'Id',
            done: onComplete,
            success: onSuccess,
            type: 'cluster',
            symbology: {
                type: 'polymorph',
                cluster: {
                    template: clusterTemplate,
                    force: true
                },
                heat: {
                    theme: ColorBrewer.getHeatColors(ColorBrewer.RED_YELLOW_BLUE),
                    property: 'MediaMensal'
                },
                style: {
                    icon: {
                        getUrl: getIcon,
                        iconSize: [50, 64],
                        iconAnchor: [25, 60],
                        labelAnchor: [25, -32]
                    }
                }
            },
            popup: {
                trigger: 'mouseover',
                template: popupTemplate,
                clusterTemplate (features) {
                    const malls = features.map(f => f.feature);

                    if (!malls.length) {
                        return;
                    }

                    const layerControls = LayerControls(name);
                    const layerType = (((layerControls.getControls(mapId) || {}).buttonGroup || {}).layerType || {}).type;
                    this.options.offset = layerType === 'decluster' ? L.point(0, -16) : L.point(0, -50);

                    return new Promise((resolve) => {
                        // Soma as marcas/vendas de todos os malls do cluster.
                        // Título do cluster = nome da camada (sem nome de centro específico).
                        const brands = _(malls)
                            .flatMap(m => m.properties.brands || [])
                            .groupBy('name')
                            .map((v, brandName) => ({
                                name: brandName,
                                icon: v[0].icon,
                                sales: _.sumBy(v, 'sales'),
                                total: _.sumBy(v, 'total')
                            }))
                            .orderBy('sales', ['desc'])
                            .value();

                        const totalStores = _.sumBy(malls, 'properties.TotalStores');
                        const totalSales = _.sumBy(malls, 'properties.Total');

                        const values = {
                            mallName: translate('Common.MallCenters'),
                            brands,
                            period: layerControls.currentPeriod,
                            totalBrands: _.size(brands),
                            totalStores,
                            totalAvgSalesInCluster: _.sumBy(brands, 'sales'),
                            totalAvgSales: totalStores ? _.sumBy(brands, 'sales') / totalStores : 0,
                            totalSales,
                            salesEfficiency: totalSales
                        };

                        resolve($(templates('map-layer-stores-in-mall-cluster-popup', values)));
                    });
                },
                options: {
                    offset: L.point(0, -50),
                    closeButton: false,
                    autoclose: true
                },
                single: true
            }
        });
    }
}

export default MallCentersLayer;



// WEBPACK FOOTER //
// ./src/scripts/layers/MallCentersLayer.js